import React, { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Popconfirm, Select, Space, Spin, Table, Tag, notification } from "antd";
import { ReloadOutlined, StopOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

import { TZ } from "../../../../config/constants";
import { formatSmartRtpNumber } from "../../../../utils/bigMath";
import {
  deleteBigPrizeAward,
  getBigPrizeMonitorList,
  inactiveBigPrizeConfig,
  inactiveBigPrizePos,
} from "../../../../controllers/V1/smartRtp/smartRtpController";

function statusTag(status) {
  const map = {
    "Winner Given": "success",
    Pending: "default",
    "Award Reserved": "processing",
    Inactive: "error",
  };
  const color =
    map[status] ||
    (String(status || "").startsWith("Awaiting order") ? "warning" : "default");
  return <Tag color={color}>{status || "-"}</Tag>;
}

export default function BigPrizePosMonitor({
  settingId,
  gameId,
  settingType = "Game",
  standalone = false,
}) {
  const [filterDate, setFilterDate] = useState(() => dayjs());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const canLoad = Boolean(settingId || gameId);

  const loadRows = useCallback(async () => {
    if (!canLoad) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getBigPrizeMonitorList({
        ...(settingId ? { setting_id: settingId } : {}),
        ...(gameId ? { game_id: gameId } : {}),
        filter_date: filterDate ? filterDate.format("YYYY-MM-DD") : undefined,
      });
      if (res?.status === true) {
        setRows(Array.isArray(res.result) ? res.result : []);
      } else {
        setRows([]);
        notification.error({
          message: "Attack Mode Monitor",
          description: res?.message || "Unable to load POS list.",
        });
      }
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canLoad, settingId, gameId, filterDate]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleDeleteAward = async (row) => {
    const res = await deleteBigPrizeAward({
      award_id: row.award_id || undefined,
      config_id: row.config_id,
      pos_number: row.pos_number,
      game_id: row.game_id || gameId || undefined,
      filter_date: filterDate.format("YYYY-MM-DD"),
    });
    if (res?.status === true) {
      notification.success({ message: "Award deleted", description: "POS can win again today." });
      loadRows();
    } else {
      notification.error({ message: "Error", description: res?.message || "Delete failed." });
    }
  };

  const handleInactivePos = async (row) => {
    const res = await inactiveBigPrizePos({
      setting_id: row.setting_id || settingId,
      config_id: row.config_id,
      pos_number: row.pos_number,
    });
    if (res?.status === true) {
      notification.success({ message: "POS inactivated", description: "Removed from configuration." });
      loadRows();
    } else {
      notification.error({ message: "Error", description: res?.message || "Inactive failed." });
    }
  };

  const handleInactiveConfig = async (row) => {
    const res = await inactiveBigPrizeConfig({
      setting_id: row.setting_id || settingId,
      config_id: row.config_id,
    });
    if (res?.status === true) {
      notification.success({ message: "Configuration inactivated" });
      loadRows();
    } else {
      notification.error({ message: "Error", description: res?.message || "Inactive failed." });
    }
  };

  if (!standalone && settingType !== "Game" && settingType !== "Global") return null;

  const columns = [
    {
      title: "Game",
      key: "game_title",
      width: 180,
      render: (_, row) => row.game?.title || row.game_title || "-",
    },
    { title: "POS Number", dataIndex: "pos_number", key: "pos_number", width: 110 },
    {
      title: "Store Name",
      key: "pos_store_name",
      width: 180,
      render: (_, row) => row.pos_store_name || "-",
    },
    {
      title: "POS User",
      key: "pos_user_name",
      render: (_, row) => row.pos_user_name || "-",
    },
    {
      title: "Big Prize",
      key: "big_prize_amount",
      render: (_, row) => {
        const kind = row.prize_kind || (row.product_name ? "Product" : "Amount");
        const amount = formatSmartRtpNumber(row.big_prize_amount || 0, 3);
        if (kind === "Product") return row.product_name || "-";
        if (kind === "Both") {
          return `${amount} + ${row.product_name || "product"}`;
        }
        return amount;
      },
    },
    {
      title: "Distribution",
      dataIndex: "distribution_type",
      key: "distribution_type",
      render: (value) => (value === "AnyTime" ? "Any time" : "Quick Prize"),
    },
    {
      title: "Time Window",
      key: "time_window",
      render: (_, row) =>
        `${row.start_time || "00:00"} – ${row.end_time || "23:59"}${
          row.distribution_type === "AnyTime" &&
          row.anytime_order_min != null &&
          row.anytime_order_max != null
            ? ` · order ${row.anytime_order_min}–${row.anytime_order_max}`
            : ""
        }`,
    },
    {
      title: "Winner Status",
      dataIndex: "winner_status",
      key: "winner_status",
      render: (value) => statusTag(value),
    },
    {
      title: "Order No",
      dataIndex: "order_no",
      key: "order_no",
      render: (value) => value || "-",
    },
    {
      title: "Config Status",
      dataIndex: "config_status",
      key: "config_status",
      render: (value) =>
        value === "A" ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 220,
      render: (_, row) => (
        <Space size="small" wrap>
          {row.award_id ? (
            <Popconfirm
              title="Delete today's award?"
              description="This POS can receive the big prize again today."
              onConfirm={() => handleDeleteAward(row)}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          ) : null}
          {row.config_status === "A" && !row.is_history ? (
            <>
              <Popconfirm
                title="Inactivate this POS?"
                description="Removes POS from this big prize configuration."
                onConfirm={() => handleInactivePos(row)}
              >
                <Button size="small" icon={<StopOutlined />}>
                  Inactive POS
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Inactivate entire configuration?"
                onConfirm={() => handleInactiveConfig(row)}
              >
                <Button size="small" danger type="dashed">
                  Inactive Config
                </Button>
              </Popconfirm>
            </>
          ) : null}
        </Space>
      ),
    },
  ];

  const toolbar = (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <span style={{ fontWeight: 500 }}>Date:</span>
      <DatePicker
        value={filterDate}
        onChange={(value) => setFilterDate(value || dayjs())}
        format="DD-MM-YYYY"
        allowClear={false}
      />
      <Button icon={<ReloadOutlined />} onClick={loadRows} loading={loading}>
        Refresh
      </Button>
    </div>
  );

  const body = (
    <>
      {!standalone ? (
        <>
          <h5 style={{ marginBottom: 8, color: "#6f42c1" }}>
          Attack Mode &amp; Time Slot — POS Monitor
          </h5>
          <p className="text-muted small mb-3">
            POS-wise status for scheduled big prizes ({TZ} calendar day).
          </p>
        </>
      ) : (
        <p className="text-muted small mb-3">
          POS-wise status for scheduled big prizes ({TZ} calendar day). Winner Given means the
          forced prize was already issued. History is kept when you add another prize for the
          same POS. Delete clears that award so that configuration can win again. Inactive
          removes the POS or configuration from future awards.{" "}
          <Link to="/smart-rtp/games">Configure big prizes in Game Settings</Link>.
        </p>
      )}
      {toolbar}
      {!canLoad ? (
        <div className="text-muted">
          {standalone
            ? "Select a game or Global settings to load POS monitor data."
            : "Save settings first to monitor POS big prize status."}
        </div>
      ) : (
        <Spin spinning={loading}>
          <Table
            rowKey={(row) =>
              `${row.config_id}-${row.pos_number}-${row.award_id || "pending"}`
            }
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            scroll={{ x: 1440 }}
            locale={{ emptyText: "No POS numbers configured for big prize." }}
            size="small"
          />
        </Spin>
      )}
    </>
  );

  if (standalone) return body;

  return (
    <div className="card shadow mb-4">
      <div className="card-body">{body}</div>
    </div>
  );
}
